import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Globe, Search, CheckCircle, ArrowRight } from 'lucide-react'
import SEOHead from '@/components/SEOHead'

// ── Data ─────────────────────────────────────────────────────────────────────

const europeCountries = [
  'Åland Islands','Austria','Azores','Balearic Islands','Belgium','Bulgaria',
  'Canary Islands','Croatia','Cyprus','Czech Republic','Denmark','Estonia',
  'Finland','France','French Guiana','Germany','Gibraltar*','Greece',
  'Guadeloupe','Guernsey*','Hungary','Iceland*','Ireland','Isle of Man*',
  'Italy','Jersey*','Latvia','Liechtenstein*','Lithuania','Luxembourg',
  'Madeira','Malta','Martinique','Mayotte','Netherlands','Norway*','Poland',
  'Portugal','Réunion','Romania','Saint Barthélemy','Saint Martin',
  'San Marino','Slovakia','Slovenia','Spain','Sweden','Switzerland*','Vatican City*',
]

const worldCountries = [
  'Australia','Brazil','Chile','Colombia','Costa Rica','El Salvador',
  'Guatemala','Hong Kong','Indonesia','Israel','Macau','New Zealand',
  'Nicaragua','Panama','Peru','Puerto Rico','Singapore','Sri Lanka',
  'Uruguay','US Virgin Islands','USA','Vietnam',
]

const worldExtraCountries = [
  'Albania','Andorra','Anguilla','Antigua & Barbuda','Argentina','Armenia',
  'Aruba','Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Bermuda',
  'Bonaire','Bosnia and Herzegovina','British Virgin Islands','Brunei',
  'Burkina Faso','Cambodia','Cameroon','Canada','Cayman Islands','China',
  "Côte d'Ivoire (Ivory Coast)",'Curaçao','Cyprus North (Turkey)','Dominica',
  'Dominican Republic','Ecuador','Egypt','Faroe Islands','Gabon','Georgia',
  'Ghana','Greenland','Grenada','Guinea','Guyana','Haiti','Honduras',
  'India','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kosovo','Kuwait',
  'Kyrgyzstan','Liberia','Madagascar','Malawi','Malaysia','Mexico','Moldova',
  'Montenegro','Montserrat','Morocco','Myanmar','Niger','Nigeria',
  'North Macedonia','Oman','Pakistan','Paraguay','Qatar','Rwanda','Saba',
  'Saint Kitts & Nevis','Saint Lucia','Saint Vincent & Grenadines',
  'Saudi Arabia','Senegal','Serbia','Seychelles','Sint Eustatius','Sint Maarten',
  'South Africa','South Korea','Suriname','Taiwan','Tajikistan','Thailand',
  'Trinidad & Tobago','Tunisia','Turkey','Turks & Caicos Islands','Uganda',
  'Ukraine','United Arab Emirates (UAE)','Uzbekistan','Zambia',
]

// Destinations with no daily charge
const freeDestinations = ['Republic of Ireland', 'Isle of Man']

const zones = [
  {
    id: 'europe',
    title: 'Go Roam in Europe',
    subtitle: 'EU & Non-EU European destinations',
    rate: '£2.50–£2.90 / day',
    rateFree: false,
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-600',
    textColor: 'text-blue-700',
    countries: europeCountries,
    note: '* Non-EU destinations are charged at £2.90/day. All others at £2.50/day.',
    count: europeCountries.length,
  },
  {
    id: 'free',
    title: 'Ireland & Isle of Man',
    subtitle: 'Roaming is an extra cost — varies from package to package',
    rate: 'Varies',
    rateFree: true,
    color: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50',
    border: 'border-green-200',
    badge: 'bg-green-600',
    textColor: 'text-green-700',
    countries: freeDestinations,
    note: 'No daily charge applies — fair use policy applies.',
    count: freeDestinations.length,
  },
  {
    id: 'world',
    title: 'Go Roam Around the World',
    subtitle: 'Popular worldwide destinations',
    rate: '£6.20 / day',
    rateFree: false,
    color: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-600',
    textColor: 'text-emerald-700',
    countries: worldCountries,
    note: 'Includes USA, Australia, New Zealand and more.',
    count: worldCountries.length,
  },
  {
    id: 'world-extra',
    title: 'Go Roam Around the World Extra',
    subtitle: 'Extended worldwide coverage',
    rate: '£8.90 / day',
    rateFree: false,
    color: 'from-amber-500 to-amber-600',
    bgLight: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-600',
    textColor: 'text-amber-700',
    countries: worldExtraCountries,
    note: 'Includes Canada, Japan, India, UAE and 90+ more destinations.',
    count: worldExtraCountries.length,
  },
]

// ── Component ─────────────────────────────────────────────────────────────────

export default function ThreeGoRoamDestinations() {
  const [search, setSearch] = useState('')
  const [activeZone, setActiveZone] = useState<string | null>(null)

  const allCountries = useMemo(() => {
    const map: Record<string, string[]> = {}
    zones.forEach((z) => {
      z.countries.forEach((c) => {
        if (!map[c]) map[c] = []
        map[c].push(z.id)
      })
    })
    return map
  }, [])

  const searchResults = useMemo(() => {
    if (!search.trim()) return null
    const q = search.toLowerCase()
    return Object.keys(allCountries)
      .filter((c) => c.toLowerCase().includes(q))
      .map((c) => ({ country: c, zones: allCountries[c] }))
      .sort((a, b) => a.country.localeCompare(b.country))
  }, [search, allCountries])

  const zoneLabel: Record<string, string> = {
    europe: 'Go Roam Europe',
    free: 'Ireland & Isle of Man',
    world: 'Around the World',
    'world-extra': 'World Extra',
  }
  const zoneBadgeColor: Record<string, string> = {
    europe: 'bg-blue-100 text-blue-700',
    free: 'bg-green-100 text-green-700',
    world: 'bg-emerald-100 text-emerald-700',
    'world-extra': 'bg-amber-100 text-amber-700',
  }

  const filteredZones = activeZone ? zones.filter((z) => z.id === activeZone) : zones

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Three Go Roam Destinations — 70+ Countries | Silux Mobile"
        description="Search the full list of Three Go Roam destinations. Check which of the 70+ Go Roam countries you can use your minutes, texts and data in with Silux Mobile. Roaming is an extra cost and varies from package to package."
        canonical="https://www.siluxmobile.co.uk/three-go-roam/destinations"
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0a3d41] via-[#0F5257] to-[#0a3d41] text-white relative overflow-hidden">
        {/* Subtle dot grid background */}
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />

        <div className="max-w-5xl mx-auto px-4 py-14 flex flex-col lg:flex-row items-center gap-10 relative z-10">
          {/* Left: branding + content */}
          <div className="flex-1 text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl font-bold mb-3 leading-tight">Go Roam Destinations</h1>
            <p className="text-white/60 text-sm max-w-md mb-7">
              Find out where our Three Network SIM works abroad and how much it costs per day
            </p>

            {/* Search */}
            <div className="max-w-sm relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search for a country..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg"
              />
            </div>
          </div>

          {/* Right: SIM card hero image */}
          <div className="flex-shrink-0 flex items-center justify-center">
            <img
              src="/images/sim-card-three.png"
              alt="Three SIM Card"
              className="h-52 sm:h-64 w-auto drop-shadow-2xl"
              style={{ filter: 'drop-shadow(0 20px 50px rgba(0,0,0,0.5))', transform: 'rotate(8deg)' }}
            />
          </div>
        </div>

        {/* Stats strip */}
        {!search && (
          <div className="max-w-5xl mx-auto px-4 pb-6 flex flex-wrap justify-center gap-3 relative z-10">
            {zones.map((z) => (
              <div key={z.id} className="bg-white/10 rounded-xl px-4 py-2 text-sm">
                <span className="font-bold text-white">{z.count}</span>
                <span className="text-white/70 ml-1">{z.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-10">

        {/* Search Results */}
        {searchResults !== null && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">
                {searchResults.length > 0
                  ? `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} for "${search}"`
                  : `No results for "${search}"`}
              </h2>
              <button onClick={() => setSearch('')} className="text-sm text-gray-400 hover:text-gray-600">
                Clear
              </button>
            </div>
            {searchResults.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {searchResults.map(({ country, zones: zids }) => (
                  <div key={country} className="px-5 py-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-900 font-medium text-sm">{country}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {zids.map((zid) => (
                        <span key={zid} className={`text-xs px-2 py-0.5 rounded-full font-medium ${zoneBadgeColor[zid]}`}>
                          {zoneLabel[zid]}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-400 text-sm">
                This country may not be covered. Check <Link to="/three-go-roam/rates" className="text-[#0F5257] underline">rates page</Link> for band information.
              </div>
            )}
          </div>
        )}

        {/* Zone filter tabs */}
        {!searchResults && (
          <>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveZone(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  activeZone === null
                    ? 'bg-[#0F5257] text-white border-[#0F5257]'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                All Zones
              </button>
              {zones.map((z) => (
                <button
                  key={z.id}
                  onClick={() => setActiveZone(activeZone === z.id ? null : z.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    activeZone === z.id
                      ? `${z.badge} text-white border-transparent`
                      : `bg-white ${z.textColor} border-gray-200 hover:border-gray-300`
                  }`}
                >
                  {z.title}
                </button>
              ))}
            </div>

            {/* Zone cards */}
            {filteredZones.map((zone) => (
              <div key={zone.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className={`bg-gradient-to-r ${zone.color} p-6 text-white`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-bold">{zone.title}</h2>
                      <p className="text-white/75 text-sm mt-0.5">{zone.subtitle}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-2xl font-bold ${zone.rateFree ? 'text-white' : 'text-white'}`}>{zone.rate}</span>
                      {!zone.rateFree && <p className="text-white/70 text-xs">per SIM / per day</p>}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-white/70" />
                    <span className="text-white/80 text-sm font-medium">{zone.count} destination{zone.count !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Countries grid */}
                <div className={`p-6 ${zone.bgLight}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {zone.countries.map((country) => (
                      <div key={country} className="flex items-center gap-1.5 text-sm text-gray-700">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        <span>{country}</span>
                      </div>
                    ))}
                  </div>
                  {zone.note && (
                    <p className={`mt-5 text-xs ${zone.textColor} italic`}>{zone.note}</p>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#0F5257] to-[#1a7a82] rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-2">Ready to Roam?</h3>
          <p className="text-white/70 mb-6 text-sm">Get a Three SIM from Silux Mobile and take your UK allowance abroad</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/three-go-roam/rates"
              className="inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white font-medium px-6 py-3 rounded-full transition-colors text-sm"
            >
              View Rates &amp; Info
            </Link>
            <Link
              to="/sim-only/roaming"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0F5257] font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-colors text-sm"
            >
              Shop Three SIMs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

      </div>
    </div>
  )
}
